import { useState } from "react";
import api, { useList, errMsg, fmtRM, fmtPct, apiFileUrl } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Play, Check, X, Eye, Plus, Download, FileDown } from "lucide-react";

const NOW = new Date();
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function PayrollDetailDialog({ payrollId, open, onOpenChange }) {
  const { data: p, loading } = useList(open && payrollId ? `/payroll/${payrollId}` : null, [payrollId, open]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="payroll-detail-dialog">
        {loading || !p ? <Spinner full /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-3">
                {p.teacher_name} — {p.payroll_year}-{String(p.payroll_month).padStart(2, "0")}
                <StatusBadge status={p.status} testId="payroll-status-badge" />
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2">
              Commission base excludes SST and School Admin Fee. Rate: {fmtPct(p.commission_rate)}
            </p>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Student / course</TableHead><TableHead>Course fee</TableHead>
                <TableHead>Rate</TableHead><TableHead className="text-right">Commission</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {p.items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-sm">{it.student_name} · {it.course_name}</TableCell>
                    <TableCell><Money value={it.commission_base} /></TableCell>
                    <TableCell>{fmtPct(it.commission_rate)}</TableCell>
                    <TableCell className="text-right"><Money value={it.teacher_commission_amount} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-sm font-mono space-y-1 bg-muted rounded-md p-4" data-testid="payroll-earnings">
                <p className="font-bold font-sans text-xs uppercase tracking-wider mb-2">Earnings</p>
                <p>Gross commission: {fmtRM(p.gross_teacher_commission)}</p>
                <p>Adjustments: {fmtRM(p.payroll_adjustment_amount)}</p>
                <p className="font-bold">Gross payable: {fmtRM(p.gross_payable_before_deductions)}</p>
              </div>
              <div className="text-sm font-mono space-y-1 bg-muted rounded-md p-4" data-testid="payroll-deductions">
                <p className="font-bold font-sans text-xs uppercase tracking-wider mb-2">Employee deductions</p>
                <p>KWSP: {fmtRM(p.kwsp_employee_deduction)}</p>
                <p>SOCSO: {fmtRM(p.socso_employee_deduction)}</p>
                <p>EIS: {fmtRM(p.eis_employee_deduction)}</p>
                <p className="font-bold border-t pt-1">Total: {fmtRM(p.total_employee_deductions)}</p>
              </div>
            </div>
            <div className="rounded-md bg-primary text-primary-foreground p-4 flex items-center justify-between">
              <span className="text-sm font-semibold">Net payable to teacher</span>
              <span className="font-mono font-bold text-lg" data-testid="payroll-net-payable">{fmtRM(p.net_payable_to_teacher)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Employer contributions (company cost, not deducted): KWSP {fmtRM(p.kwsp_employer_contribution)} ·
              SOCSO {fmtRM(p.socso_employer_contribution)} · EIS {fmtRM(p.eis_employer_contribution)} —
              total company cost {fmtRM(p.total_company_payroll_cost)}
            </p>
            {p.adjustments.length > 0 && (
              <div className="text-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Adjustments</p>
                {p.adjustments.map((a) => (
                  <p key={a.id} className="py-1 border-b last:border-0">
                    <span className="capitalize">{a.adjustment_type}</span> <Money value={a.adjustment_amount} /> — {a.reason}
                  </p>
                ))}
              </div>
            )}
            {p.logs.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {p.logs.map((l) => (
                  <p key={l.id}>{l.created_at?.slice(0, 16).replace("T", " ")} — {l.action} {l.action_note && `(${l.action_note})`}</p>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MonthYearPicker({ month, setMonth, year, setYear }) {
  return (
    <div className="flex gap-2">
      <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
        <SelectTrigger className="w-36" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
        <SelectTrigger className="w-28" data-testid="payroll-year-select"><SelectValue /></SelectTrigger>
        <SelectContent>{[NOW.getFullYear() - 1, NOW.getFullYear(), NOW.getFullYear() + 1].map((y) => (
          <SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
      </Select>
    </div>
  );
}

export default function PayrollPage({ defaultTab = "runs" }) {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const [month, setMonth] = useState(NOW.getMonth() + 1);
  const [year, setYear] = useState(NOW.getFullYear());
  const { data: payrolls, loading, reload } = useList(`/payroll?month=${month}&year=${year}`, [month, year]);
  const { data: settings, reload: reloadSettings } = useList("/payroll/settings");
  const { data: report, reload: reloadReport } = useList(`/payroll/report?month=${month}&year=${year}`, [month, year]);
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const [adjType, setAdjType] = useState("bonus");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [payRef, setPayRef] = useState("");
  const [paying, setPaying] = useState(null);
  const [commissionTeacher, setCommissionTeacher] = useState(null);
  const [commissionRate, setCommissionRate] = useState("0.55");
  const [contribTeacher, setContribTeacher] = useState(null);
  const [contrib, setContrib] = useState({});

  const refreshAll = () => { reload(); reloadReport(); };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/generate", { month, year });
      toast.success(`Generated ${data.created.length} payroll(s); ${data.skipped.length} skipped`);
      data.skipped.forEach((s) => toast.info(`${s.teacher}: ${s.reason}`));
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const decide = async (p, action) => {
    try {
      if (action === "approve") await api.post(`/payroll/${p.id}/approve`, { note: "" });
      if (action === "reject") {
        const reason = window.prompt("Reason for rejection:");
        if (reason === null) return;
        await api.post(`/payroll/${p.id}/reject`, { reason });
      }
      toast.success(`Payroll ${action}d`);
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markPaid = async () => {
    if (!payRef) { toast.error("Payment reference is required"); return; }
    try {
      await api.post(`/payroll/${paying.id}/mark-paid`, { payment_reference: payRef });
      toast.success("Payroll marked as paid");
      setPaying(null); setPayRef("");
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addAdjustment = async () => {
    if (!adjAmount || !adjReason) { toast.error("Amount and reason are required"); return; }
    try {
      await api.post(`/payroll/${adjusting.id}/adjustments`, {
        adjustment_type: adjType, amount: parseFloat(adjAmount), reason: adjReason });
      toast.success("Adjustment added — totals recalculated");
      setAdjusting(null); setAdjAmount(""); setAdjReason("");
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveCommission = async () => {
    try {
      await api.post("/payroll/settings/commission", {
        teacher_id: commissionTeacher.teacher.id, default_commission_rate: parseFloat(commissionRate) });
      toast.success("Commission rate updated");
      setCommissionTeacher(null);
      reloadSettings();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openContrib = (row) => {
    const c = row.contribution_setting || {};
    setContrib({
      kwsp_eligible: !!c.kwsp_eligible, kwsp_employee_rate: c.kwsp_employee_rate ?? 0.11, kwsp_employer_rate: c.kwsp_employer_rate ?? 0.13,
      socso_eligible: !!c.socso_eligible, socso_employee_amount: c.socso_employee_amount ?? 0, socso_employer_amount: c.socso_employer_amount ?? 0,
      eis_eligible: !!c.eis_eligible, eis_employee_amount: c.eis_employee_amount ?? 0, eis_employer_amount: c.eis_employer_amount ?? 0,
    });
    setContribTeacher(row);
  };

  const saveContrib = async () => {
    try {
      await api.post("/payroll/settings/contributions", {
        teacher_id: contribTeacher.teacher.id,
        employment_type: contribTeacher.teacher.employment_type || "part_time",
        ...Object.fromEntries(Object.entries(contrib).map(([k, v]) => [k, typeof v === "boolean" ? v : parseFloat(v || 0)])),
      });
      toast.success("Statutory contribution settings updated");
      setContribTeacher(null);
      reloadSettings();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCSV = () => {
    if (!report) return;
    const rows = [["Teacher", "Month", "Gross Commission", "Adjustments", "Employee Deductions", "Net Payable", "Employer Contribution", "Company Cost", "Status"]];
    report.payrolls.forEach((p) => rows.push([
      p.teacher_name, `${p.payroll_year}-${String(p.payroll_month).padStart(2, "0")}`,
      p.gross_teacher_commission, p.payroll_adjustment_amount || 0, p.total_employee_deductions,
      p.net_payable_to_teacher, p.total_employer_contribution, p.total_company_payroll_cost, p.status]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `payroll-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
  };

  return (
    <div data-testid="payroll-page">
      <PageHeader title="Teacher Payroll"
        description="Commission = music course fee × rate (SST and admin fee excluded). Workflow: generate → approve → mark paid. Approved payrolls are locked."
        actions={<MonthYearPicker month={month} setMonth={setMonth} year={year} setYear={setYear} />} />

      <Tabs defaultValue={defaultTab}>
        <TabsList className="h-auto mb-6 flex-wrap">
          <TabsTrigger value="runs" data-testid="tab-payroll-runs">Payroll runs</TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-payroll-generate">Generate</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-payroll-settings">Settings</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-payroll-report">Report</TabsTrigger>
        </TabsList>

        <TabsContent value="runs">
          <div className="rounded-lg border bg-card overflow-x-auto">
            {loading ? <div className="p-12 flex justify-center"><Spinner /></div> :
              !payrolls || payrolls.length === 0 ? <div className="p-6"><EmptyState text="No payroll for this month yet. Generate it from the Generate tab." /></div> : (
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Teacher</TableHead><TableHead>Gross commission</TableHead><TableHead>Deductions</TableHead>
                  <TableHead>Net payable</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody className="stagger-in">
                  {payrolls.map((p) => (
                    <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
                      <TableCell className="font-medium">{p.teacher_name}</TableCell>
                      <TableCell><Money value={p.gross_teacher_commission} /></TableCell>
                      <TableCell><Money value={p.total_employee_deductions} /></TableCell>
                      <TableCell><Money value={p.net_payable_to_teacher} className="font-bold" /></TableCell>
                      <TableCell><StatusBadge status={p.status} testId={`payroll-status-${p.id}`} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => setDetailId(p.id)} data-testid={`view-payroll-${p.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => window.open(apiFileUrl(`/payroll/${p.id}/payslip-pdf`), "_blank")}
                            data-testid={`download-payslip-${p.id}`}>
                            <FileDown className="h-3.5 w-3.5" />
                          </Button>
                          {p.status === "draft" && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => setAdjusting(p)} data-testid={`adjust-payroll-${p.id}`}>
                                <Plus className="h-3.5 w-3.5 mr-1" />Adjust
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button size="sm" onClick={() => decide(p, "approve")} data-testid={`approve-payroll-${p.id}`}>
                                    <Check className="h-3.5 w-3.5 mr-1" />Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-destructive border-destructive/30"
                                    onClick={() => decide(p, "reject")} data-testid={`reject-payroll-${p.id}`}>
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                          {p.status === "approved" && (
                            <Button size="sm" variant="secondary" onClick={() => setPaying(p)} data-testid={`pay-payroll-${p.id}`}>
                              Mark paid
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="generate">
          <div className="rounded-lg border bg-card p-8 max-w-xl">
            <h3 className="text-lg font-semibold mb-2">Generate payroll for {MONTHS[month - 1]} {year}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Builds draft payrolls from <strong>fully paid invoices</strong> for the month. Commission base is the
              music course fee only — SST and the School Admin Fee are excluded. Approved or paid payrolls are locked and will be skipped.
            </p>
            <Button onClick={generate} disabled={busy} data-testid="generate-payroll-button">
              <Play className="h-4 w-4 mr-1.5" /> {busy ? "Generating..." : "Generate payroll"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="rounded-lg border bg-card overflow-x-auto">
            {!settings ? <div className="p-12 flex justify-center"><Spinner /></div> : (
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Teacher</TableHead><TableHead>Commission rate</TableHead>
                  <TableHead>KWSP</TableHead><TableHead>SOCSO</TableHead><TableHead>EIS</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {settings.map((row) => (
                    <TableRow key={row.teacher.id} data-testid={`settings-row-${row.teacher.id}`}>
                      <TableCell className="font-medium">{row.teacher.full_name}</TableCell>
                      <TableCell>{row.commission_setting ? fmtPct(row.commission_setting.default_commission_rate) : "55% (default)"}</TableCell>
                      <TableCell><StatusBadge status={row.contribution_setting?.kwsp_eligible ? "active" : "inactive"} /></TableCell>
                      <TableCell><StatusBadge status={row.contribution_setting?.socso_eligible ? "active" : "inactive"} /></TableCell>
                      <TableCell><StatusBadge status={row.contribution_setting?.eis_eligible ? "active" : "inactive"} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5 justify-end">
                          <Button variant="outline" size="sm" data-testid={`set-commission-${row.teacher.id}`}
                            onClick={() => { setCommissionTeacher(row); setCommissionRate(String(row.commission_setting?.default_commission_rate ?? 0.55)); }}>
                            Commission
                          </Button>
                          <Button variant="outline" size="sm" data-testid={`set-contrib-${row.teacher.id}`} onClick={() => openContrib(row)}>
                            KWSP/SOCSO/EIS
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="report">
          {!report ? <Spinner full /> : (
            <>
              <div className="flex justify-end mb-4">
                <Button variant="outline" onClick={exportCSV} data-testid="export-csv-button">
                  <Download className="h-4 w-4 mr-1.5" />Export CSV
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-lg border bg-card p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total net payable</p>
                  <p className="text-2xl font-bold font-display mt-1" data-testid="report-net-total">{fmtRM(report.totals.total_net_payable)}</p>
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Employer contributions</p>
                  <p className="text-2xl font-bold font-display mt-1">{fmtRM(report.totals.total_employer_contribution)}</p>
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total company cost</p>
                  <p className="text-2xl font-bold font-display mt-1">{fmtRM(report.totals.total_company_cost)}</p>
                </div>
              </div>
              <div className="rounded-lg border bg-card overflow-x-auto">
                {report.payrolls.length === 0 ? <div className="p-6"><EmptyState /></div> : (
                  <Table>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead>Teacher</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead>
                      <TableHead>Net</TableHead><TableHead>Employer cont.</TableHead><TableHead>Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {report.payrolls.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.teacher_name}</TableCell>
                          <TableCell><Money value={p.gross_teacher_commission} /></TableCell>
                          <TableCell><Money value={p.total_employee_deductions} /></TableCell>
                          <TableCell><Money value={p.net_payable_to_teacher} /></TableCell>
                          <TableCell><Money value={p.total_employer_contribution} /></TableCell>
                          <TableCell><StatusBadge status={p.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <PayrollDetailDialog payrollId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />

      {/* Mark paid dialog */}
      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader><DialogTitle className="font-display">Mark payroll as paid</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {paying?.teacher_name} — net payable {fmtRM(paying?.net_payable_to_teacher)}
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Payment reference</Label>
            <Input data-testid="payment-ref-input" value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. bank transfer ref" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
            <Button onClick={markPaid} data-testid="confirm-mark-paid-button">Confirm paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment dialog */}
      <Dialog open={!!adjusting} onOpenChange={(o) => !o && setAdjusting(null)}>
        <DialogContent data-testid="adjustment-dialog">
          <DialogHeader><DialogTitle className="font-display">Payroll adjustment — {adjusting?.teacher_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={adjType} onValueChange={setAdjType}>
                <SelectTrigger data-testid="adjustment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">Bonus (+)</SelectItem>
                  <SelectItem value="deduction">Deduction (−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount (RM)</Label>
              <Input data-testid="adjustment-amount-input" type="number" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Input data-testid="adjustment-reason-input" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjusting(null)}>Cancel</Button>
            <Button onClick={addAdjustment} data-testid="confirm-adjustment-button">Add adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission dialog */}
      <Dialog open={!!commissionTeacher} onOpenChange={(o) => !o && setCommissionTeacher(null)}>
        <DialogContent data-testid="commission-dialog">
          <DialogHeader><DialogTitle className="font-display">Commission rate — {commissionTeacher?.teacher.full_name}</DialogTitle></DialogHeader>
          <Select value={commissionRate} onValueChange={setCommissionRate}>
            <SelectTrigger data-testid="commission-rate-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0.52">52%</SelectItem>
              <SelectItem value="0.55">55%</SelectItem>
              <SelectItem value="0.58">58%</SelectItem>
              <SelectItem value="0.6">60%</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommissionTeacher(null)}>Cancel</Button>
            <Button onClick={saveCommission} data-testid="save-commission-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contributions dialog */}
      <Dialog open={!!contribTeacher} onOpenChange={(o) => !o && setContribTeacher(null)}>
        <DialogContent className="max-w-lg" data-testid="contributions-dialog">
          <DialogHeader><DialogTitle className="font-display">KWSP / SOCSO / EIS — {contribTeacher?.teacher.full_name}</DialogTitle></DialogHeader>
          {[
            { key: "kwsp", label: "KWSP (EPF)", rate: true },
            { key: "socso", label: "SOCSO", rate: false },
            { key: "eis", label: "EIS", rate: false },
          ].map((g) => (
            <div key={g.key} className="border rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{g.label}</p>
                <Switch data-testid={`${g.key}-eligible-switch`} checked={!!contrib[`${g.key}_eligible`]}
                  onCheckedChange={(v) => setContrib((c) => ({ ...c, [`${g.key}_eligible`]: v }))} />
              </div>
              {contrib[`${g.key}_eligible`] && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{g.rate ? "Employee rate (e.g. 0.11)" : "Employee amount (RM)"}</Label>
                    <Input data-testid={`${g.key}-employee-input`} type="number" step="0.01"
                      value={contrib[g.rate ? `${g.key}_employee_rate` : `${g.key}_employee_amount`] ?? ""}
                      onChange={(e) => setContrib((c) => ({ ...c, [g.rate ? `${g.key}_employee_rate` : `${g.key}_employee_amount`]: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{g.rate ? "Employer rate (e.g. 0.13)" : "Employer amount (RM)"}</Label>
                    <Input data-testid={`${g.key}-employer-input`} type="number" step="0.01"
                      value={contrib[g.rate ? `${g.key}_employer_rate` : `${g.key}_employer_amount`] ?? ""}
                      onChange={(e) => setContrib((c) => ({ ...c, [g.rate ? `${g.key}_employer_rate` : `${g.key}_employer_amount`]: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContribTeacher(null)}>Cancel</Button>
            <Button onClick={saveContrib} data-testid="save-contributions-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
